export interface APIResponse<T = any> {
	success: boolean;
	data?: T;
	error?: string;
	message?: string;
}

export interface PaginatedResponse<T> extends APIResponse<T[]> {
	pagination?: {
		page: number;
		limit: number;
		total: number;
		hasNext: boolean;
		hasPrevious: boolean;
	};
}

export interface GoogleAPIError {
	code: number;
	message: string;
	status: string;
}

export interface RetryOptions {
	maxRetries: number;
	retryDelay: number;
	backoffFactor: number;
}

export interface RateLimitInfo {
	limit: number;
	remaining: number;
	reset: number;
}

export interface APIClientOptions {
	baseURL?: string;
	timeout?: number;
	retries?: RetryOptions;
	debug?: boolean;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface Logger {
	debug(message: string, meta?: any): void;
	info(message: string, meta?: any): void;
	warn(message: string, meta?: any): void;
	error(message: string, meta?: any): void;
}

export class ConsoleLogger implements Logger {
	constructor(private level: LogLevel = 'info') {}

	private shouldLog(level: LogLevel): boolean {
		const levels = ['debug', 'info', 'warn', 'error'];
		return levels.indexOf(level) >= levels.indexOf(this.level);
	}

	debug(message: string, meta?: any): void {
		if (this.shouldLog('debug')) {
			console.debug(`[DEBUG] ${message}`, meta || '');
		}
	}

	info(message: string, meta?: any): void {
		if (this.shouldLog('info')) {
			console.info(`[INFO] ${message}`, meta || '');
		}
	}

	warn(message: string, meta?: any): void {
		if (this.shouldLog('warn')) {
			console.warn(`[WARN] ${message}`, meta || '');
		}
	}

	error(message: string, meta?: any): void {
		if (this.shouldLog('error')) {
			console.error(`[ERROR] ${message}`, meta || '');
		}
	}
}
